// apps/dashboard/src/components/Analytics.tsx
'use client';

import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { useAnalyticsConsent } from '@/lib/use-consent';
import { isSecretBearingPath } from '@/lib/auth-secret-urls';

/**
 * Google Analytics 4: dependency-free, env-driven, and opt-in.
 *
 * Renders nothing until NEXT_PUBLIC_GA_ID is set to a real measurement ID
 * (the `.env.example` placeholder `G-XXXXXXXXXX` is treated as "not set") AND the
 * visitor has accepted analytics cookies (see lib/consent.ts). Revoking later stops
 * new loads but cannot unload gtag from the page already open; it is gone on the next load.
 * To enable: set NEXT_PUBLIC_GA_ID=G-XXXXXXXXXX in this app's env and rebuild.
 */
export function Analytics() {
  const hasConsent = useAnalyticsConsent();
  const gaId = process.env.NEXT_PUBLIC_GA_ID;
  const pathname = usePathname();
  if (!hasConsent || !gaId || gaId === 'G-XXXXXXXXXX') return null;
  // gtag('config') reports page_location with its query string, which on these pages is a
  // single-use auth code. GA is not loaded there at all.
  if (isSecretBearingPath(pathname ?? '')) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${gaId}');`}
      </Script>
    </>
  );
}
